import type { FieldCatalogEntry, ValueKind } from '@packages/ui/patterns/CriteriaRuleBuilder';

export type { FieldCatalogEntry, ValueKind };

/** Infer value editor kind from legacy criteria type systemName. */
export function inferUdcValueKind(systemName: string): ValueKind {
  if (systemName.includes('Country')) return 'country';
  if (systemName.includes('State')) return 'state';
  if (systemName.includes('City')) return 'city';
  return 'text';
}

export function normalizeCriteriaTypeToCatalogEntry(item: {
  displayName?: string | null;
  systemName?: string | null;
}): FieldCatalogEntry | null {
  const code = item.systemName?.trim();
  if (!code) return null;
  return {
    code,
    label: item.displayName?.trim() || code,
    valueKind: inferUdcValueKind(code),
    operators: 'inherit',
  };
}

/** Port of legacy validateCriteria — verbatim messages. */
export function validateCriteriaTree(criteria: unknown): Record<string, unknown> {
  const errors: Record<string, unknown> = {};
  const root = criteria as {
    LogicalOperatorType?: string | null;
    Conditions?: Array<{
      SubCriteria?: { LogicalOperatorType?: string | null; Conditions?: Array<{ Field?: string | null }> };
    }>;
  };

  if (!root?.LogicalOperatorType) {
    return errors;
  }

  let hasConditionErrors = false;
  const conditions = root.Conditions ?? [];

  conditions.forEach((condition, index) => {
    const conditionErrors: Record<string, unknown> = {};
    let hasSubCriteriaErrors = false;

    if (condition.SubCriteria?.LogicalOperatorType) {
      const subConditionsErrors: Record<string, unknown>[] = [];
      const subConditions = condition.SubCriteria.Conditions ?? [];

      subConditions.forEach((subCondition, subIndex) => {
        const subErrors: Record<string, unknown> = {};
        if (
          subCondition.Field === null ||
          subCondition.Field === undefined ||
          String(subCondition.Field).trim() === ''
        ) {
          subErrors.Field = 'Required Field';
          hasSubCriteriaErrors = true;
          subConditionsErrors[subIndex] = subErrors;
        }
      });

      if (hasSubCriteriaErrors) {
        conditionErrors.SubCriteria = { Conditions: subConditionsErrors };
      }
    }

    if (Object.keys(conditionErrors).length > 0) {
      if (!errors.Conditions) {
        errors.Conditions = [];
      }
      (errors.Conditions as unknown[])[index] = conditionErrors;
      hasConditionErrors = true;
    }
  });

  if (!hasConditionErrors) {
    delete errors.Conditions;
  }

  return errors;
}

export function serializeUserDefinedCriteria(value: unknown): string | null {
  if (value == null || typeof value !== 'object') return null;
  const criteria = value as {
    Conditions?: Array<{
      SubCriteria?: { Conditions?: Array<{ Field?: string | null }> };
    }>;
  };
  const firstField = criteria.Conditions?.[0]?.SubCriteria?.Conditions?.[0]?.Field;
  if (firstField == null || String(firstField).trim() === '') {
    return null;
  }
  return JSON.stringify(value);
}
